import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReportMetadata } from '../../types';
import { heuteLokalIso } from '../../components/planungsphase/planungLogic';
import { ladeSteckbriefQuellen, lesefehlerText } from './quellen';
import { baueSteckbrief, type ProjektSteckbrief, type SteckbriefQuellen } from './steckbrief';

/**
 * Der Projektsteckbrief eines Projekts fuer eine Folgeunterlage.
 *
 * ═══ LESEN JA, SCHREIBEN NIE ══════════════════════════════════════════════
 * Dieser Hook laedt und rechnet, sonst nichts. Er legt keine Planungsphase an
 * (dasselbe Versprechen wie `usePlanung`) und schreibt in kein Modul. Jede
 * Uebernahme in die Unterlage laeuft ueber einen Knopf — eine Vorbelegung,
 * die sich beim Laden selbst speichert, mutiert Bestandsdatensaetze
 * rueckwirkend (Lehre aus Handbuch und Planungsphase).
 *
 * ═══ WARUM „FUER" IM ZUSTAND STEHT UND KEINE LAEDT-FLAGGE ═════════════════
 * Ein synchron im Effekt gesetztes `laedt: true` waere eine Kaskadenrenderung
 * — der Lint dieses Projekts verbietet sie. Steht das Projekt IM Zustand, ist
 * „noch nicht geladen" schlicht „gehoert zu einem anderen Projekt" (Muster
 * `useProjektBestand` im A+S-Modul).
 *
 * ═══ `bereit` IST TEIL DER AUSSAGE ════════════════════════════════════════
 * Vor der Antwort ist `steckbrief` `null` — und `null` heisst „noch nicht
 * gelesen", nicht „es gibt nichts". Eine Leiste, die aus dem Fehlen etwas
 * folgert („keine Planungsphase"), muss `bereit` abfragen.
 *
 * ═══ PROJEKTDATEN WERDEN BEIM RECHNEN GEMISCHT, NICHT BEIM LADEN ══════════
 * `metadata` ist bei jedem Rendern ein frisches Objekt (`report.metadata`).
 * Hinge der Ladeeffekt daran, ginge bei jedem Tastendruck in den Stammdaten
 * eine Datenbankrunde los. Deshalb laedt der Effekt nur die Planungsdaten
 * (`projectId`) und `baueSteckbrief` mischt die jeweils aktuellen Projekt-
 * daten im `useMemo` dazu — Aenderungen an Bauherr oder Bauzeit sind damit
 * sofort im Steckbrief, ohne Neuladen.
 *
 * `standTag` wird beim Eintreffen der Antwort festgehalten (Ortszeit), nicht
 * beim Rendern: kein `new Date()` im Render, und Fristvergleiche springen
 * nicht zwischen zwei Zeichnungen um.
 */

interface Gelesen {
    fuer: string;
    quellen: SteckbriefQuellen;
    standTag: string;
}

export interface ProjektSteckbriefStand {
    /** `null`, solange nicht `bereit`. */
    steckbrief: ProjektSteckbrief | null;
    /** Erst `true`, wenn die Daten zu GENAU diesem Projekt vorliegen. */
    bereit: boolean;
    /** Lesefehler als Satz — der Steckbrief liegt trotzdem vor (fail-open), mit `ungeklaert` und Grund. */
    fehler: string | null;
    /** Planungsdaten neu lesen, etwa nach „Übernommen" oder nach Aenderungen in der Planungsphase. */
    neuLaden: () => void;
}

export const useProjektSteckbrief = (
    projectId: string,
    metadata: Partial<ReportMetadata> | null,
): ProjektSteckbriefStand => {
    const [gelesen, setGelesen] = useState<Gelesen | null>(null);
    const [lauf, setLauf] = useState(0);

    const neuLaden = useCallback(() => setLauf(n => n + 1), []);

    useEffect(() => {
        let gilt = true;
        // `ladeSteckbriefQuellen` lehnt nie ab (fail-open); Lesefehler stehen
        // in den Quellen. Die Projektdaten kommen unten im `useMemo` dazu.
        void ladeSteckbriefQuellen(projectId, null).then(quellen => {
            if (gilt) setGelesen({ fuer: projectId, quellen, standTag: heuteLokalIso() });
        });
        return () => { gilt = false; };
    }, [projectId, lauf]);

    const bereit = gelesen !== null && gelesen.fuer === projectId;

    const steckbrief = useMemo(
        () => (bereit && gelesen ? baueSteckbrief({ ...gelesen.quellen, metadata }, gelesen.standTag) : null),
        [bereit, gelesen, metadata],
    );

    return {
        steckbrief,
        bereit,
        fehler: bereit && gelesen ? lesefehlerText(gelesen.quellen.lesefehler) : null,
        neuLaden,
    };
};
